/**
 * What an Independent Supporter's core token says about them.
 *
 * api.core writes one `election` claim per linked election. A JWT serialiser turns repeated claims
 * into an array, but .NET writes a SINGLE claim as a bare string, so a supporter with one election
 * arrives as `election: "2003"` and one with several as `election: ["2003", "2011"]`. Reading only
 * one of those shapes is how a supporter ends up with no elections at all.
 */
export interface ISupporterClaims {
	supporterId: number | null;
	candidateId: number | null;
	partyId: number | null;
	/** Distinct, positive, in token order. */
	electionIds: number[];
}

const toPositiveInt = (value: unknown): number | null => {
	if (typeof value !== 'string' && typeof value !== 'number') return null;
	const n = Number(String(value).trim());
	return Number.isInteger(n) && n > 0 ? n : null;
};

/** Normalise a claim that may be absent, a single value or an array into a list of positive ints. */
export const claimList = (claim: unknown): number[] => {
	const raw = Array.isArray(claim) ? claim : claim == null ? [] : [claim];
	const out: number[] = [];
	raw.forEach((value) => {
		const n = toPositiveInt(value);
		if (n != null && !out.includes(n)) out.push(n);
	});
	return out;
};

export const parseSupporterClaims = (user: Record<string, unknown> | null | undefined): ISupporterClaims => ({
	supporterId: toPositiveInt(user?.SupporterId),
	candidateId: toPositiveInt(user?.CandidateId),
	partyId: toPositiveInt(user?.PartyId),
	electionIds: claimList(user?.election),
});
